import React, { useCallback, useMemo, useState } from 'react';
import type { Transition } from 'framer-motion';
import { AnimatePresence, motion } from 'framer-motion';

import { StandardModal } from '@components/main-components/modals/StandardModal';
import { FeedPostModalMain } from '@components/modals/FeedPostModal/FeedPostModalMain';
import { FeedPostModalSoundSelect } from '@components/modals/FeedPostModal/FeedPostModalSoundSelect';
import { useFeedPost } from '@components/modals/FeedPostModal/useFeedPost';
import { useModal } from '@context/modal';
import { useToast } from '@context/toast';
import type { FeedPostModalMainSoundFragment } from '@graphql/reactQuery';
import { DEFAULT_ERROR_TOAST_MESSAGE_TRY_AGAIN_LATER } from '@utils/constants';

enum FeedPostModalStep {
  Main,
  SoundSelect,
}

const TRANSITION: Transition = {
  duration: 0.25,
  ease: 'easeInOut',
};

export function FeedPostModal() {
  const { closeModal } = useModal();
  const [step, setStep] = useState(FeedPostModalStep.Main);
  const [release, setRelease] = useState<FeedPostModalMainSoundFragment>();
  const [postText, setPostText] = useState('');
  const { openToast } = useToast();

  const { isLoading: isMutating, mutateAsync } = useFeedPost();

  const handleSubmitClicked = useCallback(async () => {
    if (!release) {
      return;
    }
    try {
      const result = await mutateAsync({
        input: {
          content: postText,
          releaseId: release.id,
        },
      });
      if (result.data.createReleasePost.__typename === 'MutationCreateReleasePostSuccess') {
        closeModal();
        openToast({ text: 'Your post has been created', variant: 'success' });
      } else {
        openToast({ text: DEFAULT_ERROR_TOAST_MESSAGE_TRY_AGAIN_LATER, variant: 'error' });
      }
    } catch (error) {
      openToast({ text: DEFAULT_ERROR_TOAST_MESSAGE_TRY_AGAIN_LATER, variant: 'error' });
    }
  }, [postText, release, mutateAsync, closeModal, openToast]);

  const title = useMemo(
    () => (step === FeedPostModalStep.Main ? 'Write a Post' : 'Select a Sound'),
    [step],
  );

  return (
    <StandardModal
      closeModal={() => {
        closeModal();
      }}
      bodyClassName="px-0 overflow-hidden"
      containerClassName="pb-0"
      bodyWithoutPadding
      bodyWithoutPaddingBottom
      title={title}
      body={
        <div className="h-80 ">
          <AnimatePresence initial={false} mode="popLayout">
            {step === FeedPostModalStep.Main && (
              <motion.div
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                key="main"
                transition={TRANSITION}
                className="flex h-full w-full flex-col gap-6 px-4 pb-4 lt:px-6 lt:pb-6"
              >
                <FeedPostModalMain
                  isMutating={isMutating}
                  onPostTextChange={setPostText}
                  postText={postText}
                  onChangeSoundClicked={() => {
                    setStep(FeedPostModalStep.SoundSelect);
                  }}
                  onSubmitClicked={handleSubmitClicked}
                  release={release}
                />
              </motion.div>
            )}
            {step === FeedPostModalStep.SoundSelect && (
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                key="sound-select"
                transition={TRANSITION}
                className="flex h-full w-full flex-col gap-6 pb-4  lt:pb-6"
              >
                <FeedPostModalSoundSelect
                  onBackClicked={() => {
                    setStep(FeedPostModalStep.Main);
                  }}
                  onReleaseSelected={release => {
                    setRelease(release);
                  }}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      }
    />
  );
}
